import { useState } from 'react';
import { Button } from '@vivaahealth/design-system';
import { api } from 'common';

import { FullPagePrimitive } from './FullPagePrimitive';
import { Question } from '../../../components/Question';
import * as store from '../../../reducers/store';

interface Props {
  initialFields: api.PrevisitFormField.PrevisitFormFieldWithConfig[];
  formFields: api.PrevisitFormField.PrevisitFormFieldWithConfig[];
  formSets: store.FormSets;
  patient: api.Patient.Patient | null;
  practicePreference: api.Previsit.PracticePreference;
  setFieldValue: (fieldId: string, value: string) => void;
  saveFieldValue: (fieldId: string, value: string) => Promise<void>;
  selectedFormSetId: string;
  saveButtonLabel: string;
  encounter?: api.Encounter.Encounter | null;
  navigateToNextPage: () => Promise<void>;
  validateAndSavePageFields: (
    fields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
  ) => Promise<{ success: boolean }>;
  pending?: boolean;
}

export const Content = ({
  encounter,
  initialFields,
  formFields,
  formSets,
  patient,
  practicePreference,
  setFieldValue,
  selectedFormSetId,
  saveButtonLabel,
  navigateToNextPage,
  validateAndSavePageFields,
  pending,
}: Props) => {
  const [fields, setFields] = useState(initialFields);
  if (!fields.length) return null;

  /**
   * We don't want to use local fields for full page primitives since
   * we can't prevent them making server interactions. In that case we do
   * want to update store on spot/in sync. That's why we are
   * not using the local fields for full page primitives here.
   */
  const [firstInitialField] = initialFields;
  if (
    api.PrevisitFormTemplateFieldConfig.FULL_PAGE_PRIMITIVES.includes(
      firstInitialField.primitive,
    )
  ) {
    return (
      <FullPagePrimitive
        field={firstInitialField}
        encounter={encounter}
        updateFieldValue={setFieldValue}
        navigateToNextPage={navigateToNextPage}
      />
    );
  }

  const validateAndNavigateToNextPage = async () => {
    const { success } = await validateAndSavePageFields(fields);
    if (success) {
      fields.forEach((field) => setFieldValue(field.id, field.value));
      await navigateToNextPage();
    }
  };

  const updateFieldValue = (fieldId: string, value: string) => {
    setFields((prevFields) => {
      return prevFields.map((field) => {
        if (field.id === fieldId) {
          return {
            ...field,
            value,
          };
        }
        return field;
      });
    });
  };

  return (
    <div>
      {fields.map((field) => {
        return (
          <div key={field.id}>
            <Question
              disabled
              field={field}
              fields={formFields}
              formSets={formSets}
              navigateToNextPage={validateAndNavigateToNextPage}
              patient={patient}
              practicePreference={practicePreference}
              saveFieldValue={() => {}}
              updateFieldValue={(value: string) =>
                updateFieldValue(field.id, value)
              }
              selectedFormSetId={selectedFormSetId}
              useAlternateEnum={false}
            />
          </div>
        );
      })}
      <Button
        fullWidth
        onClick={validateAndNavigateToNextPage}
        disabled={pending}
      >
        {saveButtonLabel}
      </Button>
    </div>
  );
};
